import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Bell,
  Menu,
  X,
  Home,
  LayoutDashboard,
  PlusCircle,
  User,
  Users,
  UserPlus,
  LogIn,
  LogOut,
  Settings,
  HelpCircle,
  Search,
  Store,
  PanelLeft,
  Share2,
  ExternalLink,
  Trophy,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToUserNotifications } from '../api/notifications';
import WelcomeGuideModal from '../components/WelcomeGuideModal';
import ShareProfileModal from '../components/ShareProfileModal';
import FounderLink from '../components/FounderLink';
import MobileBottomNav from '../components/MobileBottomNav';
import ThemePicker from '../components/ThemePicker';
import { WELCOME_STORAGE_KEY } from '../constants/founder';

const Layout = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [shareProfileOpen, setShareProfileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && !localStorage.getItem(WELCOME_STORAGE_KEY)) {
        setWelcomeOpen(true);
      }
    } catch {
      /* private mode */
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setUnreadCount(0);
      return;
    }

    const unsubscribe = subscribeToUserNotifications(currentUser.uid, (notifs) => {
      setUnreadCount(notifs.filter((n) => !n.read).length);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const dismissWelcome = () => {
    try {
      localStorage.setItem(WELCOME_STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setWelcomeOpen(false);
  };

  const openGuide = () => {
    setWelcomeOpen(true);
    closeMobileMenu();
  };

  const navLinkTopClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
      isActive
        ? 'text-tech-cyan bg-tech-cyan/10 border border-tech-cyan/30 shadow-[0_0_15px_rgba(34,211,238,0.15)] backdrop-blur-sm'
        : 'text-tech-text-muted hover:text-tech-text hover:bg-white/[0.06]'
    }`;

  const navLinkSidebarClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
      isActive
        ? 'text-tech-cyan bg-gradient-to-r from-tech-cyan/10 to-transparent border border-tech-cyan/20 shadow-[0_0_15px_rgba(34,211,238,0.1)] nav-active-indicator'
        : 'text-tech-text-muted hover:text-tech-text hover:bg-white/[0.04]'
    }`;

  const authedNavItems = [
    { to: '/', label: 'Home', icon: Home, end: true },
    { to: '/task-dashboard', label: 'Task hub', icon: LayoutDashboard, end: false },
    { to: '/task-hatch', label: 'Create task', icon: PlusCircle, end: false },
    { to: '/taskman', label: 'TaskAssign', icon: Users, end: false },
    { to: '/marketplace', label: 'Task Marketplace', icon: Store, end: false },
    { to: '/task-buddies', label: 'Task Buddies', icon: UserPlus, end: false },
    { to: '/ranking-guide', label: 'Ranks & Guide', icon: Trophy, end: false },
    { to: '/tasker-profile', label: 'Profile', icon: User, end: false },
    { to: '/profile-settings', label: 'Settings', icon: Settings, end: false },
  ];

  const guestNavItems = [
    { to: '/', label: 'Home', icon: Home, end: true },
    { to: '/marketplace', label: 'Marketplace', icon: Store, end: false },
    { to: '/ranking-guide', label: 'Ranks & Guide', icon: Trophy, end: false },
    { to: '/search-guides', label: 'Search guides', icon: Search, end: false },
  ];

  const navItems = currentUser ? authedNavItems : guestNavItems;

  const userInitial =
    currentUser?.displayName?.trim()?.charAt(0)?.toUpperCase() ||
    currentUser?.email?.charAt(0)?.toUpperCase() ||
    '?';

  const sidebarItemBase =
    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 text-tech-text-muted hover:text-tech-text hover:bg-white/[0.04]';

  const SidebarNav = ({ onItemClick }: { onItemClick?: () => void }) => (
    <>
      <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-tech-text-muted/80">
        Workspace
      </p>
      <nav className="flex flex-col gap-0.5">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={navLinkSidebarClass}
            onClick={onItemClick}
          >
            <item.icon className="w-[18px] h-[18px] shrink-0 opacity-90" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {currentUser && (
        <>
          <p className="px-3 mt-6 mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-tech-text-muted/80">
            Profile link
          </p>
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => {
                setShareProfileOpen(true);
                onItemClick?.();
              }}
              className={`${sidebarItemBase} w-full text-left`}
            >
              <Share2 className="h-[18px] w-[18px] shrink-0 opacity-90" aria-hidden />
              <span>Share public profile</span>
            </button>
            <NavLink
              to={`/tasker/${currentUser.uid}`}
              target="_blank"
              rel="noopener noreferrer"
              className={navLinkSidebarClass}
              onClick={onItemClick}
            >
              <ExternalLink className="w-[18px] h-[18px] shrink-0 opacity-90" aria-hidden />
              <span>Open public page</span>
            </NavLink>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="flex min-h-screen min-h-[100dvh] flex-col bg-tech-surface text-tech-text">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-tech-cyan focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-tech-darker focus:shadow-app-md"
      >
        Skip to content
      </a>
      {currentUser ? (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {/* Mobile sidebar backdrop */}
          {mobileMenuOpen && (
            <button
              type="button"
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
              aria-label="Close menu"
              onClick={closeMobileMenu}
            />
          )}

          {/* Desktop + mobile drawer sidebar */}
          <aside
            className={`fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col border-r border-white/[0.07] bg-tech-sidebar/95 glass-panel-heavy shadow-app-sidebar transition-transform duration-300 ease-out lg:static lg:z-0 lg:h-auto lg:min-h-screen lg:translate-x-0 lg:flex-shrink-0 ${
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
            }`}
          >
            <div className="shrink-0 border-b border-white/[0.06] px-4 py-3.5">
              <NavLink
                to="/"
                className="flex min-w-0 items-center gap-2.5 font-display text-base font-semibold tracking-tight text-tech-text hover:text-tech-cyan transition-colors"
                onClick={closeMobileMenu}
              >
                <img src="/logos/logo192.png" alt="" className="h-8 w-8 shrink-0 object-contain hover:scale-105 transition-transform" />
                <span className="truncate animated-gradient-text tracking-wide">GNK Continuum</span>
              </NavLink>
              <p className="text-[9px] font-medium uppercase tracking-[0.15em] text-tech-text-muted/50 mt-1.5 pl-[42px]">
                Global Network for Knowledge
              </p>
            </div>

            <div className="custom-scrollbar flex-1 overflow-y-auto px-3 py-4">
              <SidebarNav onItemClick={closeMobileMenu} />
            </div>

            <div className="shrink-0 border-t border-white/[0.06] p-3 space-y-3">
              <div className="rounded-lg border border-white/[0.06] bg-tech-elevated/80 px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-tech-text-muted mb-1">
                  Signed in
                </p>
                <p className="truncate text-xs text-tech-text" title={currentUser.email || undefined}>
                  {currentUser.email}
                </p>
              </div>
              <button
                type="button"
                onClick={async () => {
                  await logout();
                  closeMobileMenu();
                  navigate('/');
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 text-sm font-medium text-tech-text-muted transition-colors hover:border-tech-cyan/30 hover:bg-tech-cyan-dim hover:text-tech-cyan"
              >
                <LogOut className="h-4 w-4" />
                Log out
              </button>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wider text-tech-text-muted/90 mb-1.5 px-0.5">
                  Founder
                </p>
                <FounderLink variant="footer" />
              </div>
            </div>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-white/[0.07] bg-tech-surface/92 px-3 backdrop-blur-xl shadow-app-header sm:px-4 lg:px-6">
              <div className="flex min-w-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen((o) => !o)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-tech-text-muted transition-colors hover:bg-white/[0.05] hover:text-tech-cyan lg:hidden"
                  aria-label={mobileMenuOpen ? 'Close navigation' : 'Open navigation'}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
                <div className="hidden items-center gap-2 text-tech-text-muted sm:flex lg:hidden">
                  <PanelLeft className="h-4 w-4 opacity-50" />
                  <span className="truncate text-sm font-medium text-tech-text">Menu</span>
                </div>
                <p className="hidden font-display text-sm font-semibold tracking-tight text-tech-text/90 lg:block">
                  Workspace
                </p>
              </div>

              <div className="flex items-center gap-0.5 sm:gap-1">
                <ThemePicker />
                <button
                  type="button"
                  onClick={openGuide}
                  className="rounded-lg p-2 text-tech-text-muted transition-colors hover:bg-white/[0.05] hover:text-tech-cyan"
                  title="What is this app? How to use"
                  aria-label="Help: what is this app and how to use"
                >
                  <HelpCircle className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/notifications');
                    closeMobileMenu();
                  }}
                  className="relative rounded-lg p-2 text-tech-text-muted transition-colors hover:bg-white/[0.05] hover:text-tech-cyan"
                  title="Notifications"
                  aria-label="Notifications"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-tech-cyan px-1 text-[10px] font-bold text-tech-darker">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                <div className="relative ml-1">
                  <button
                    type="button"
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="hidden h-9 w-9 items-center justify-center rounded-full bg-gradient-to-tr from-tech-cyan/20 to-blue-500/20 border border-tech-cyan/30 text-sm font-bold text-tech-cyan sm:flex transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] hover:border-tech-cyan focus:outline-none focus:ring-2 focus:ring-tech-cyan focus:ring-offset-2 focus:ring-offset-tech-surface avatar-ring-glow pointer-events-auto"
                    title={currentUser.displayName || currentUser.email || 'Account'}
                    aria-expanded={profileMenuOpen}
                    aria-haspopup="true"
                  >
                    {userInitial}
                  </button>

                  {/* Glassmorphism Profile Dropdown */}
                  {profileMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40 sm:hidden"
                        onClick={() => setProfileMenuOpen(false)}
                      />
                      <div
                        className="fixed inset-0 z-40 hidden sm:block bg-transparent"
                        onClick={() => setProfileMenuOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl border border-tech-cyan/30 bg-tech-surface/90 backdrop-blur-xl shadow-[0_0_30px_rgba(0,0,0,0.5)] shadow-tech-cyan/10 ring-1 ring-black ring-opacity-5 focus:outline-none z-50 transform opacity-100 scale-100 transition-all duration-200">
                        <div className="px-4 py-3 border-b border-tech-border/30">
                          <p className="text-sm text-tech-text-muted">Signed in as</p>
                          <p className="truncate text-sm font-bold text-tech-text mt-0.5">
                            {currentUser.email}
                          </p>
                        </div>
                        <div className="py-1">
                          <button
                            type="button"
                            onClick={() => {
                              setProfileMenuOpen(false);
                              navigate('/profile-settings');
                            }}
                            className="flex w-full items-center px-4 py-2 text-sm text-tech-text hover:bg-tech-cyan/10 hover:text-tech-cyan transition-colors"
                          >
                            <Settings className="mr-3 h-4 w-4" />
                            Account Settings
                          </button>
                        </div>
                        <div className="border-t border-tech-border/30 py-1">
                          <button
                            type="button"
                            onClick={async () => {
                              setProfileMenuOpen(false);
                              await logout();
                              navigate('/');
                            }}
                            className="flex w-full items-center px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                          >
                            <LogOut className="mr-3 h-4 w-4" />
                            Log out
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </header>

            <main id="main-content" tabIndex={-1} className="app-shell-bg min-h-0 flex-1 overflow-y-auto outline-none pb-16 lg:pb-0">
              <Outlet />
            </main>

            <footer className="shrink-0 border-t border-white/[0.07] bg-tech-sidebar/95 backdrop-blur-md hidden lg:block">
              <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs text-tech-text-muted">© {new Date().getFullYear()} GNK Continuum</span>
                <button
                  type="button"
                  onClick={openGuide}
                  className="text-left text-xs text-tech-text-muted transition-colors hover:text-tech-cyan sm:text-right"
                >
                  Help &amp; guide
                </button>
              </div>
            </footer>
            <MobileBottomNav />
          </div>
        </div>
      ) : (
        <>
          <header className="sticky top-0 z-50 border-b border-white/[0.07] bg-tech-surface/95 backdrop-blur-xl shadow-app-header">
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
              <NavLink
                to="/"
                className="flex min-w-0 items-center gap-2.5 font-display text-lg font-semibold tracking-tight text-tech-text transition-colors hover:text-tech-cyan"
                onClick={closeMobileMenu}
              >
                <img src="/logos/logo192.png" alt="" className="h-8 w-8 shrink-0 object-contain" />
                <span className="truncate">GNK Continuum</span>
              </NavLink>

              <nav className="hidden items-center gap-1 md:flex">
                {navItems.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.end} className={navLinkTopClass}>
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </nav>

              <div className="flex items-center gap-1 sm:gap-2">
                <ThemePicker />
                <button
                  type="button"
                  onClick={openGuide}
                  className="rounded-lg p-2 text-tech-text-muted transition-colors hover:bg-white/[0.05] hover:text-tech-cyan"
                  title="Help"
                  aria-label="Help"
                >
                  <HelpCircle className="h-5 w-5" />
                </button>
                <div className="hidden items-center gap-2 sm:flex">
                  <NavLink
                    to="/signup"
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-tech-blue to-tech-cyan px-4 py-2 text-sm font-semibold text-white shadow-app-sm transition-opacity hover:opacity-95"
                  >
                    Sign up
                  </NavLink>
                  <NavLink
                    to="/login"
                    className="inline-flex items-center gap-2 rounded-lg border border-white/[0.12] bg-white/[0.03] px-3 py-2 text-sm font-medium text-tech-text transition-colors hover:border-tech-cyan/40 hover:bg-tech-cyan-dim"
                  >
                    <LogIn className="h-4 w-4" />
                    Login
                  </NavLink>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="rounded-lg p-2 text-tech-text-muted transition-colors hover:bg-white/[0.05] hover:text-tech-cyan md:hidden"
                  aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>

            {mobileMenuOpen && (
              <div className="animate-slide-down border-t border-white/[0.06] bg-tech-sidebar md:hidden">
                <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-4">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={navLinkTopClass}
                      onClick={closeMobileMenu}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                  <NavLink to="/signup" className={navLinkTopClass} onClick={closeMobileMenu}>
                    <UserPlus className="h-4 w-4" />
                    <span>Sign up</span>
                  </NavLink>
                  <NavLink to="/login" className={navLinkTopClass} onClick={closeMobileMenu}>
                    <LogIn className="h-4 w-4" />
                    <span>Login</span>
                  </NavLink>
                </nav>
              </div>
            )}
          </header>

          <main id="main-content" tabIndex={-1} className="app-shell-bg flex-1 min-h-[50vh] outline-none">
            <Outlet />
          </main>

          <footer className="border-t border-white/[0.07] bg-tech-sidebar/90 backdrop-blur-md">
            <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5">
              <div className="flex flex-col gap-3 text-sm text-tech-text-muted sm:flex-row sm:items-center sm:justify-between">
                <span>© {new Date().getFullYear()} GNK Continuum</span>
                <button
                  type="button"
                  onClick={openGuide}
                  className="text-left transition-colors hover:text-tech-cyan sm:text-right"
                >
                  Help &amp; guide
                </button>
              </div>
              <div>
                <p className="mb-2 text-xs text-tech-text-muted">Founder</p>
                <FounderLink variant="footer" />
              </div>
            </div>
          </footer>
        </>
      )}

      <WelcomeGuideModal open={welcomeOpen} onClose={dismissWelcome} showAuthLinks={!currentUser} />

      {currentUser && (
        <ShareProfileModal
          open={shareProfileOpen}
          onClose={() => setShareProfileOpen(false)}
          userId={currentUser.uid}
          displayName={currentUser.displayName}
        />
      )}
    </div>
  );
};

export default Layout;
